import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight, FlaskConical, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../authentication/authenticationSlice";
import { SampleDataStatusResponse } from "../contracts/SampleDataStatusResponse";
import { ROUTES } from "../../../routes";

const DISMISSED_KEY = "tp-sample-tour-dismissed";
const VISITED_KEY = "tp-sample-tour-visited";

interface TourStop {
  key: string;
  label: string;
  hint: string;
  to: string;
}

const readVisited = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(VISITED_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
};

/**
 * Guided highlights over the loaded sample dataset — each stop deep-links to a
 * real screen showing one high-value feature. Dismissal is remembered per load
 * (keyed by the set's creation time), so reloading the samples brings it back.
 */
const SampleDataTour: React.FC<{ status: SampleDataStatusResponse }> = ({ status }) => {
  const { t } = useTranslation();
  const isOrgAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === (status.createdAt ?? ""),
  );
  const [visited, setVisited] = useState<string[]>(readVisited);

  if (dismissed) return null;

  const highlights = status.highlights;
  const stops: TourStop[] = [];

  if (highlights?.attendanceSessionId && highlights.attendanceDate) {
    stops.push({
      key: "attendance",
      label: t("sampleData.tour.attendance", "Mark today's group class attendance"),
      hint: t("sampleData.tour.attendanceHint", "Three weeks are already recorded — today is yours."),
      to: `${ROUTES.ATTENDANCE}/${highlights.attendanceSessionId}/${highlights.attendanceDate}`,
    });
  }

  if (isOrgAdmin && highlights?.packMemberId) {
    stops.push({
      key: "pack",
      label: t("sampleData.tour.pack", "See a session pack balance"),
      hint: t("sampleData.tour.packHint", "A 10-session pack, partly used by past visits."),
      to: ROUTES.MEMBER_BILLING.replace(":id", highlights.packMemberId),
    });
  }

  if (isOrgAdmin) {
    stops.push({
      key: "monthEnd",
      label: t("sampleData.tour.monthEnd", "Review last month's drafted payment"),
      hint: t("sampleData.tour.monthEndHint", "Month-end drafts wait for your confirmation."),
      to: `${ROUTES.BILLING}?tab=month-end`,
    });
  }

  if (isOrgAdmin && highlights?.overdueMemberId) {
    stops.push({
      key: "overdue",
      label: t("sampleData.tour.overdue", "Chase an overdue payment"),
      hint: t("sampleData.tour.overdueHint", "Overdue payments can be reminded in one click."),
      to: ROUTES.MEMBER_BILLING.replace(":id", highlights.overdueMemberId),
    });
  }

  stops.push({
    key: "portal",
    label: t("sampleData.tour.portal", "Discover the member portal"),
    hint: t("sampleData.tour.portalHint", "What your members see — schedule, absences, materials."),
    to: ROUTES.WEBSITE,
  });

  const markVisited = (key: string) => {
    const next = visited.includes(key) ? visited : [...visited, key];
    setVisited(next);
    localStorage.setItem(VISITED_KEY, JSON.stringify(next));
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, status.createdAt ?? "");
    setDismissed(true);
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <FlaskConical size={20} className="text-primary-40 dark:text-primary-90" />
          <h3 className="text-grey-10 dark:text-grey-90">
            {t("sampleData.tour.title", "Explore the sample data")}
          </h3>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("sampleData.tour.dismiss", "Hide the tour")}
          className="rounded-md p-1 text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
        >
          <X size={18} />
        </button>
      </div>
      <p className="mt-1 text-sm text-grey-30 dark:text-grey-70">
        {t(
          "sampleData.tour.subtitle",
          "A few stops that show what Timeplanned does with real-looking data.",
        )}
      </p>
      <div className="mt-4 space-y-2">
        {stops.map((stop) => {
          const done = visited.includes(stop.key);
          return (
            <div
              key={stop.key}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-grey-90 px-3 py-2.5 dark:border-primary-20"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    done
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-primary-90 text-primary-30 dark:bg-primary-30 dark:text-primary-90"
                  }`}
                >
                  {done ? <Check size={14} /> : <ChevronRight size={14} />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-grey-10 dark:text-grey-90">{stop.label}</p>
                  <p className="text-xs text-grey-30 dark:text-grey-70">{stop.hint}</p>
                </div>
              </div>
              <Link
                to={stop.to}
                onClick={() => markVisited(stop.key)}
                className={done ? "btn btn-secondary" : "btn btn-primary"}
              >
                <span>{t("sampleData.tour.open", "Open")}</span>
                <ChevronRight size={16} />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SampleDataTour;
